import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { environment } from '../../../src/app/environments/environment';
import { catchError, retry } from 'rxjs/operators';
import { Extrato } from '../models/extrato.model';
import { VerificacaoExistenciaExtratoRequest } from '../models/verificacaoExistenciaExtratoRequest.model';

@Injectable({
  providedIn: 'root',
})

export class ExtratoService {
  private apiUrl = `${environment.advContabilApi}/api/Extrato`;

  private extratosSource = new BehaviorSubject<Extrato[]>([]);
  extratos$ = this.extratosSource.asObservable();

  private currentExtratoSource = new BehaviorSubject<Extrato | null>(null);
  currentExtrato$ = this.currentExtratoSource.asObservable();

  private filesSource = new BehaviorSubject<File[]>([]);
  files$ = this.filesSource.asObservable();

  constructor(private http: HttpClient) { }

  setExtratos(extratos: Extrato[]) {
    this.extratosSource.next(extratos);
  }

  getExtratos() {
    return this.extratos$;
  }

  getExtratosComLinhasExtrato(usuarioId: string): Observable<Extrato[]> {
    const params = new HttpParams()
      .set('usuarioId', usuarioId)
      .set('_', Date.now().toString());

    return this.http.get<Extrato[]>(`${this.apiUrl}/ListarExtratosComLinhasExtratos`, { params }).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  setFiles(files: File[]) {
    this.filesSource.next(files);
  }

  getFiles() {
    return this.files$;
  }

  setCurrentExtrato(extrato: Extrato) {
    this.currentExtratoSource.next(extrato);
  }

  getCurrentExtrato() {
    return this.currentExtrato$;
  }

  getLinhasExtratoOfx(formData: FormData): Observable<Extrato[]> {
    return this.http.post<Extrato[]>(`${this.apiUrl}/RetornarLinhasExtrato`, formData);
  }

  importarExtratosDTO(extratosDTO: Extrato[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/ImportarExtratos`, extratosDTO, { responseType: 'text' }).pipe(
      catchError((error) => {
        console.error('Erro ao importar extratos', error);
        throw error;
      })
    );
  }

  excluirExtrato(extratoId: string, usuarioId: string): Observable<string> {
    const params = new HttpParams()
      .set('extratoId', extratoId)
      .set('usuarioId', usuarioId);

    return this.http.delete(`${this.apiUrl}/ExcluirExtrato`, { params, responseType: 'text' }).pipe(
      catchError((error) => {
        console.error('Erro ao excluir extrato', error);
        throw error;
      })
    );
  }

  verificarExtratoExistente(request: VerificacaoExistenciaExtratoRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/VerificarExtratoExistente`, request).pipe(retry(1));
  }

  resetState() {
    this.setFiles([]);
    this.setExtratos([]);
    this.setCurrentExtrato(null);
  }

  baixarPlanilhaModelo(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/DownloadPlanilhaModelo`, {
      responseType: 'blob',
    });
  }

  getLinhasExtratoPlanilha(formData: FormData): Observable<any> {
    return this.http.post<Extrato[]>(`${this.apiUrl}/RetornarExtratoPlanilha`, formData);
  }

  atualizarContasContabeisLinhasExtrato(payload: {
    usuarioId: string;
    linhas: {
      linhaExtratoId: number;
      novaContaContabilId: string;
      permiteAlterarContaContrapartida: boolean;
    }[];
  }): Observable<string> {
    return this.http.put(
      `${this.apiUrl}/AtualizarContasContabeisLinhasExtrato`,
      payload,
      { responseType: 'text' }
    ).pipe(
      catchError((error) => {
        console.error('Erro ao atualizar contas do extrato', error);
        throw error;
      })
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Erro: ${error.error.message}`;
    } else {
      errorMessage = `Erro Código: ${error.status}\nMensagem: ${error.message}`;
    }
    console.error(errorMessage);

    return throwError(() => new Error(errorMessage));
  }
}
